import { useEffect } from 'react';
import { Sun, Moon, LogOut, BookOpen } from 'lucide-react';
import clsx from 'clsx';
import { useAppStore } from '../../store/useAppStore';
import { LogoMark } from '../common/Logo';
import { useAuthStore } from '../../store/useAuthStore';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { navGroups, groupForPath, itemMatchesPath, type NavGroup } from './navigation';

export default function Rail() {
  const {
    activePanel,
    setActivePanel,
    activeRailGroup,
    setRailGroup,
    toggleRailGroup,
    togglePanel,
    darkMode,
    toggleDarkMode,
  } = useAppStore();
  const { user, logout, hasRole, hasModule } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  const canSee = (roles?: string[], module?: string) =>
    (!roles || roles.some((r) => hasRole(r as any))) && (!module || hasModule(module));

  // A group shows only if the user can reach something inside it.
  const visibleGroups = navGroups.filter(
    (group) =>
      canSee(group.requiredRoles, group.requiredModule) &&
      (group.items.length === 0 ||
        group.items.some((item) => canSee(item.requiredRoles, item.requiredModule)))
  );

  // Orient a deep link ON ARRIVAL ONLY: landing on /admin/users from a
  // permalink should open Administration. Deliberately not keyed on pathname —
  // the secondary rail collapses once you pick something, and re-running this
  // on every navigation would immediately reopen it and undo that.
  useEffect(() => {
    if (activePanel) return;
    const owner = groupForPath(window.location.pathname);
    if (owner) setRailGroup(owner.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleGroupClick = (group: NavGroup) => {
    if (group.items.length === 0) {
      // A direct destination: collapse the flyout and give the page the width.
      setRailGroup(null);
      setActivePanel(null);
      if (group.route) navigate(group.route);
      return;
    }
    // Switching groups closes a slideout belonging to the group being left —
    // otherwise you end up looking at Release Notes with Administration
    // selected, which is just confusing.
    if (group.id !== activeRailGroup && activePanel) {
      const ownsPanel = group.items.some((item) => item.panel === activePanel);
      if (!ownsPanel && activePanel !== 'docs') setActivePanel(null);
    }
    toggleRailGroup(group.id);
  };

  const isGroupActive = (group: NavGroup) => {
    if (activeRailGroup === group.id) return true;
    if (activePanel) return group.items.some((item) => item.panel === activePanel);
    if (group.route === '/') return location.pathname === '/';
    return group.items.some((item) => itemMatchesPath(item, location.pathname));
  };

  return (
    <div className="flex flex-col w-14 bg-rail text-white shrink-0 h-screen">
      {/* Brand. The mark stands alone here because a 56px rail genuinely
          forbids the wordmark — so it gets an accessible name of its own,
          rather than the aria-hidden it carries beside the lockup. */}
      <Link
        to="/"
        aria-label="Heuristic Systems"
        title="Heuristic Systems"
        className="flex items-center justify-center h-14 border-b border-white/10 text-accent-light hover:text-white transition-colors"
      >
        <LogoMark className="h-7 w-7" />
      </Link>

      {/* User avatar */}
      {user && (
        <div className="flex flex-col items-center py-2 border-b border-white/10">
          {user.avatarUrl ? (
            <img
              src={user.avatarUrl}
              alt={user.name}
              className="w-8 h-8 rounded-full border-2 border-accent/50"
              referrerPolicy="no-referrer"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-accent/20 text-accent flex items-center justify-center text-xs font-bold">
              {user.name?.charAt(0)?.toUpperCase() || '?'}
            </div>
          )}
        </div>
      )}

      {/* Primary groups */}
      <nav className="flex flex-col items-center gap-1 py-2 flex-1">
        {visibleGroups.map((group) => (
          <button
            key={group.id}
            onClick={() => handleGroupClick(group)}
            className={clsx(
              'relative flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-150 group',
              isGroupActive(group)
                ? 'bg-accent text-white'
                : 'text-white/60 hover:bg-rail-hover hover:text-white'
            )}
            title={group.label}
            aria-expanded={group.items.length > 0 ? activeRailGroup === group.id : undefined}
          >
            {isGroupActive(group) && (
              <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-white rounded-r" />
            )}
            <group.icon size={20} />

            {/* Tooltip — suppressed while this group's flyout is showing the
                label already. */}
            {activeRailGroup !== group.id && (
              <span className="absolute left-14 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-50">
                {group.label}
              </span>
            )}
          </button>
        ))}
      </nav>

      {/* Utilities — not destinations, so they stay out of the groups */}
      <nav className="flex flex-col items-center gap-1 py-2 border-t border-white/10">
        <button
          onClick={toggleDarkMode}
          className="relative flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-150 group text-white/60 hover:bg-rail-hover hover:text-white"
          title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
        >
          {darkMode ? <Sun size={20} /> : <Moon size={20} />}
          <span className="absolute left-14 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-50">
            {darkMode ? 'Light Mode' : 'Dark Mode'}
          </span>
        </button>

        <button
          onClick={() => togglePanel('docs')}
          className={clsx(
            'relative flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-150 group',
            activePanel === 'docs'
              ? 'bg-accent text-white'
              : 'text-white/60 hover:bg-rail-hover hover:text-white'
          )}
          title="Documentation"
        >
          <BookOpen size={20} />
          <span className="absolute left-14 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-50">
            Documentation
          </span>
        </button>

        <button
          onClick={handleLogout}
          className="relative flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-150 group text-white/60 hover:bg-red-500/20 hover:text-red-400"
          title="Sign out"
        >
          <LogOut size={20} />
          <span className="absolute left-14 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-50">
            Sign out
          </span>
        </button>
      </nav>
    </div>
  );
}
